import { IMG } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { WAVELENGTHS, STATS } from "@/data/content";
import { Reveal, SectionHeading, Overline, Btn, AddToCartButton } from "@/components/common/ui";
import { EditorialMarquee } from "@/components/common/Marquees";
import { ResearchBacked } from "@/components/product/ResearchBacked";

const RESEARCH = [
  { title: "Red Light & Collagen", source: "Journal of Clinical & Aesthetic Dermatology", finding: "Red light therapy significantly improves wrinkles, fine lines and skin texture by stimulating fibroblast cells to produce more collagen and elastin." },
  { title: "Blue Light & Acne", source: "Journal of the American Academy of Dermatology", finding: "Blue light effectively kills Propionibacterium acnes, the bacteria responsible for breakouts, while reducing inflammation." },
  { title: "Near-Infrared & Repair", source: "NASA Research on LED Therapy", finding: "Near-infrared light (800–900nm) accelerates wound healing and reduces inflammation by penetrating deeper to stimulate circulation and cell regeneration." },
];

export default function Science() {
  return (
    <div data-testid="science-page" className="bg-[#FFFCFA]">
      <header className="relative overflow-hidden border-b border-[#F2E8E6] bg-[#232222] py-28 text-[#FFFCFA] md:py-40 grain">
        <img src={M("science_hero")} data-media-slot="science_hero" data-media-type="image" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Overline>Photobiomodulation</Overline>
          <h1 className="mt-5 max-w-3xl font-display text-5xl font-light leading-[0.98] sm:text-7xl">
            The science of <span className="italic text-[#FBC4A8]">light</span>, made simple.
          </h1>
          <p className="mt-6 max-w-xl text-lg font-light text-[#FFFCFA]">
            Originally developed by NASA for wound healing, LED therapy is backed by decades of dermatological research. Here's exactly how it transforms your skin.
          </p>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-6 md:px-12 lg:grid-cols-2">
          <Reveal className="hover-zoom overflow-hidden rounded-[2rem] border border-[#F2E8E6]">
            <img src={M("science_research")} data-media-slot="science_research" data-media-type="image" alt="Dermatology research" className="h-[34rem] w-full object-cover" />
          </Reveal>
          <div>
            <SectionHeading overline="How It Works" title="It starts in your cells." />
            <p className="mt-6 text-base font-light leading-relaxed text-[#8A7F86]">
              LED therapy works through <strong className="font-medium text-[#2C2B2C]">photobiomodulation</strong>, different wavelengths of light penetrate the skin at varying depths, triggering cellular responses that promote healing, collagen production and antibacterial effects.
            </p>
            <p className="mt-4 text-base font-light leading-relaxed text-[#8A7F86]">
              The light energy is absorbed by your mitochondria, the powerhouse of the cell, stimulating ATP production, which fuels cell repair and regeneration. Crucially, LED light contains no UV rays, so it's gentle and non-damaging.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {STATS.map(([v, l]) => (
                <div key={l} className="border-l-2 border-[#FBC4A8] pl-4">
                  <p className="font-display text-3xl text-[#2C2B2C]">{v}</p>
                  <p className="text-xs uppercase tracking-wider text-[#8A7F86]">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <SectionHeading overline="The Wavelengths" title="Every colour, a purpose." className="mb-14" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WAVELENGTHS.map((w, i) => (
              <Reveal key={w.name} delay={i * 0.08} className="rounded-[2rem] border border-[#F2E8E6] bg-white p-7">
                <span className="block h-12 w-12 rounded-full" style={{ backgroundColor: w.swatch, boxShadow: `0 0 30px ${w.swatch}` }} />
                <h3 className="mt-6 font-display text-xl text-[#2C2B2C]">{w.name} <span className="text-sm text-[#FBC4A8]">{w.nm}</span></h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#8A7F86]">{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacked />

      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12">
          <SectionHeading overline="Research-Backed" title="The evidence is clear." className="mb-14" />
          <div className="space-y-6">
            {RESEARCH.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.08} className="grid gap-4 rounded-[2rem] border border-[#F2E8E6] bg-[#FFFCFA] p-8 md:grid-cols-[1fr_2fr]">
                <div>
                  <h3 className="font-display text-2xl text-[#2C2B2C]">{r.title}</h3>
                  <p className="mt-2 text-xs uppercase tracking-wider text-[#FBC4A8]">{r.source}</p>
                </div>
                <p className="text-base font-light leading-relaxed text-[#8A7F86]">{r.finding}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <AddToCartButton sku="single" label="Experience It Yourself" testid="science-add" />
            <Btn to="/blog" variant="outline" testid="science-journal">Read the Journal</Btn>
          </div>
        </div>
      </section>
    </div>
  );
}
